import type { RiskAssessment } from '@nestjs-boilerplate/types';
import { AlertCircle, Download, Loader2 } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  downloadUploadedDocument,
  type UploadedDocument,
  useUploadedDocuments,
} from '../api/risk-assessments';

interface DocumentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessment: RiskAssessment | null;
}

function formatDateTime(date: Date | string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
    .format(new Date(date))
    .replace(',', '');
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDocumentType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function DocumentsModal({ open, onOpenChange, assessment }: DocumentsModalProps) {
  const {
    data: uploadedDocuments,
    isLoading,
    error,
  } = useUploadedDocuments(assessment?.id ?? '', open && !!assessment);

  if (!assessment) return null;

  const documentRequests = assessment.documentRequests ?? [];

  // Group uploaded documents by document request ID
  const uploadedByRequestId = (uploadedDocuments ?? []).reduce(
    (acc, doc) => {
      if (!acc[doc.documentRequestId]) {
        acc[doc.documentRequestId] = [];
      }
      acc[doc.documentRequestId].push(doc);
      return acc;
    },
    {} as Record<string, UploadedDocument[]>,
  );

  const handleDownload = async (fileId: string, filename: string) => {
    try {
      await downloadUploadedDocument(assessment.id, fileId, filename);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            Documents Requested
          </DialogTitle>
          <p className="text-[14px] text-muted-foreground">Client Name: {assessment.clientName}</p>
        </DialogHeader>

        <div className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading documents...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-8 text-destructive">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>Failed to load documents</span>
            </div>
          ) : documentRequests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No document requests found for this assessment.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-medium">Document Type Requested</TableHead>
                  <TableHead className="font-medium">Date & Time Requested</TableHead>
                  <TableHead className="font-medium">Documents Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documentRequests.map((doc) => {
                  const uploads = uploadedByRequestId[doc.id] ?? [];
                  const hasUploads = uploads.length > 0;

                  return (
                    <TableRow key={doc.id}>
                      <TableCell className="text-[#051923]">
                        {formatDocumentType(doc.documentType)}
                      </TableCell>
                      <TableCell className="text-[#051923]">
                        {formatDateTime(doc.dateRequested)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2">
                          <span className="text-[#051923]">
                            {hasUploads ? `Yes (${uploads.length})` : 'No'}
                          </span>
                          {hasUploads && (
                            <div className="flex flex-col gap-1">
                              {uploads.map((file) => (
                                <button
                                  key={file.id}
                                  type="button"
                                  onClick={() => handleDownload(file.id, file.originalFilename)}
                                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                                >
                                  <Download className="h-3 w-3" />
                                  <span className="truncate max-w-[200px]">
                                    {file.originalFilename}
                                  </span>
                                  <span className="text-muted-foreground text-xs">
                                    ({formatFileSize(file.sizeBytes)})
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
